import {
    Box,
    Grid,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import {
    useGlobalPlaylists,
    usePlaylistAudios,
    usePlaylists,
} from "JS/React/Hooks/Playlist";
import { useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "../Layout";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import qs from "qs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import { getThumbnailUrl } from "JS/React/Hooks/Common/ImageUpload";
import { mediaService } from "JS/Services/Media";
import {
    ContentWithMedia,
    EventActions,
    EventCategories,
    EventNames,
    GiftWithMedia,
    MiniPlayerOptions,
} from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useGlobalPlaylistControls } from "JS/React/Hooks/PlaylistControls";
import { getDexieConnectionRefresh } from "JS/Database/Dexie";
import { config } from "JS/Config";
import {
    useContentPlayingHandlers,
    usePlayerHandlers,
} from "JS/React/Hooks/MediaPlayer";
import { PlayerActionBtns } from "JS/React/Components/MediaPlayer/PlayerActionBtns";
import { useOwnedBlobUrl } from "JS/React/Hooks/Media";
import { useMiniPlayerHandlers } from "JS/React/Hooks/MiniPlayer";
import { useResumeRecentlyPlaylist } from "JS/React/Hooks/ResumeRecently";
import { getCurrentUrl, willHandOffToMiniPlayer } from "JS/Helpers/MiniPlayerHelper";
import { PLAYER_PROGRESS_INTERVAL } from "JS/Helpers/Constants";
import {
    SentGiftType,
    getPlayerPlayedTimeToLog,
    toSentGift,
} from "JS/Models/Firebase/GiftDashboard";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import { usePlaybackAudios } from "JS/Helpers/PlaylistHelpers";

export interface PlaylistPlayerProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

const PlayerContainer = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    height: "60vh",
    [theme.breakpoints.down("lg")]: {
        height: "50vh",
    },
    [theme.breakpoints.down("sm")]: {
        height: "30vh",
    },
}));

const ForwardBackItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    color: theme.palette.grey.A100,
    cursor: "pointer",
}));

const FwdBackTypo = styled(Typography)({
    fontSize: "18px",
});

const FwdBackIcon = styled(FontAwesomeIcon)({
    fontSize: "18px",
});

const BackIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginRight: theme.spacing(2),
}));

const FwdIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginLeft: theme.spacing(2),
}));

const Grey800Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[800],
}));

export const PlaylistPlayer = (props: PlaylistPlayerProps) => {
    const [mediaURL, setMediaURL] = useState("");
    const [thumbnail, setThumbnail] = useState<string>(null);
    const [isSingleLoop, setSingleLoop] = useState(false);
    const [downloadedAudioUrl, setDownloadedAudioUrl] = useState<string>("");
    const [isAudioDownloaded, setIsAudioDownloaded] = useState<boolean>(null);

    const [currentPlayingState, setCurrentPlayingState] = useState(true);
    const [playingState, setPlayingState] = useState(false);
    const { handlePlayerPause, handlePlayerPlay } = useContentPlayingHandlers(
        (val) => {
            setPlayingState(val);
            setCurrentPlayingState(val);
        },
    );
    const { miniPlayer, onClickMiniPlayer } = useMiniPlayerHandlers();

    const { loading: playlistLoading } = usePlaylistAudios();
    const { audios: allAudios } = useGlobalAudios();
    const { userPlaylists: playlists } = useGlobalPlaylists();

    const { loading: audioLoading } = useAudios();
    const { loading: playlistsLoading } = usePlaylists(
        playlists && playlists.length > 0,
    );
    const [forceReloadKey, setForceReloadKey] = useState(0);

    const params: any = useParams();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { id } = params;
    const location = useLocation();

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { index = 0, bundleIndex = 0, scrollTo = 0 } = parsedQueryString;

        return {
            index: index.toString(),
            bundleIndex: bundleIndex.toString(),
            scrollTo: scrollTo,
        };
    }, [location?.search]);
    const setQueryParams = (params: Partial<typeof queryParams>) => {
        navigate({
            pathname: location.pathname,
            search: qs.stringify({ ...queryParams, ...params }),
        }, { replace: true });
    };
    const mediaIndex = +queryParams.index;
    const bundleIndex = +queryParams.bundleIndex;

    const playlist = useMemo(() => {
        return playlists?.find((p) => p?.id === +id);
    }, [playlists, id]);

    const { playlistControls, toggleGlobalShuffle, toggleGlobalLoop } =
        useGlobalPlaylistControls();

    const {
        playlistAudios,
        currentAudio,
        rmgLoading,
        rmpLoading,
        isGift,
        getAudioByIndex,
        sortedMappedAudios,
        title,
    } = usePlaybackAudios(
        playlist,
        allAudios,
        playlistControls.shuffle,
        mediaIndex,
        bundleIndex,
        setQueryParams,
        false,
    );

    useEffect(() => {
        if (playlistAudios?.length - 1 < mediaIndex)
            setQueryParams({ index: "0", bundleIndex: "0" });
    }, [playlistAudios]);

    const {
        playerRef,
        setIsReady,
        handleContentCompleted,
        onMediaPlayerReady,
        onPlayerProgress,
        isReadyToPlay,
        setIsReadyToPlay,
    } = useResumeRecentlyPlaylist(playlistAudios, id, mediaIndex, bundleIndex);

    const { handleChangeSeek } = usePlayerHandlers(playerRef);

    const next = () => {
        setIsAudioDownloaded(null);
        const isBundle = currentAudio?.media?.length > 1;
        setIsReady(false);
        setIsReadyToPlay(false);
        setCurrentPlayingState(true);

        if (playlistAudios?.length === 1) {
            setForceReloadKey((prev) => prev + 1);
            return;
        }

        setQueryParams({
            index:
                isBundle && bundleIndex < currentAudio?.media?.length - 1
                    ? mediaIndex.toString()
                    : ((mediaIndex + 1) % playlistAudios?.length).toString(),
            bundleIndex:
                isBundle && bundleIndex < currentAudio?.media?.length - 1
                    ? (bundleIndex + 1).toString()
                    : "0",
        });
    };
    const previous = () => {
        setIsAudioDownloaded(null);
        const isBundle = currentAudio?.media?.length > 1;
        setIsReady(false);
        setIsReadyToPlay(false);

        if (playlistAudios?.length === 1) {
            setForceReloadKey((prev) => prev + 1);
            return;
        }

        let newMediaIndex = mediaIndex;
        if (mediaIndex === 0) {
            if (isBundle) {
                if (bundleIndex === 0)
                    newMediaIndex = playlistAudios?.length - 1;
                else newMediaIndex = mediaIndex;
            } else newMediaIndex = playlistAudios?.length - 1;
        } else if (!isBundle || bundleIndex === 0)
            newMediaIndex = mediaIndex - 1;
        let newBundleIndex = bundleIndex;
        if (isBundle && bundleIndex !== 0) {
            newBundleIndex = bundleIndex - 1;
        } else if (newMediaIndex !== mediaIndex) {
            const prevAudio = getAudioByIndex(newMediaIndex);
            if (prevAudio?.media?.length > 1)
                newBundleIndex = prevAudio?.media?.length - 1;
            else newBundleIndex = 0;
        }
        setCurrentPlayingState(true);
        setQueryParams({
            index: newMediaIndex?.toString(),
            bundleIndex: newBundleIndex?.toString(),
        });
    };

    const fetchThumbnails = async () => {
        const audio = currentAudio;
        const thumb = await getThumbnailUrl(
            audio.image_url_postfix,
            audio.image_file_name,
        );

        setThumbnail(thumb);
    };

    const isBundle = +currentAudio?.no_of_files > 1;

    useFirebaseEvents(currentAudio, isAudioDownloaded, isBundle, bundleIndex);

    const getPlayerAction = () => {
        return getPlayerPlayedTimeToLog(
            playerRef,
            isBundle ? bundleIndex + 1 : null,
        );
    };

    const isReceivedMemberAudio = isGift(currentAudio?.nid);

    const { logGiftCompleted } = useGiftDashboard(
        toSentGift(currentAudio, bundleIndex),
        SentGiftType.AUDIO,
        currentAudio && "sender" in currentAudio
            ? currentAudio?.sender?.member_id
            : null,
        isReceivedMemberAudio && currentPlayingState && !miniPlayer?.url,
        getPlayerAction,
    );

    useEffect(() => {
        if (
            currentAudio &&
            "media" in currentAudio &&
            currentAudio.media?.[bundleIndex]
        ) {
            const { media_url_postfix, media_file_name, media_bucket_name } =
                currentAudio.media[bundleIndex];

            if (!media_bucket_name || !media_url_postfix || !media_file_name) {
                return;
            }

            fetchThumbnails();
            mediaService
                .getSignedUrl(
                    media_bucket_name,
                    media_url_postfix,
                    media_file_name,
                )
                .then((response) => setMediaURL(response.media_url))
                .catch((err) => console.log("Error fetching media URL:", err));
        }
    }, [currentAudio?.nid, mediaIndex, bundleIndex]);

    useEffect(() => {
        if (currentAudio) {
            const { nid } = currentAudio;
            const contentNid = isBundle ? `${nid}-${bundleIndex + 1}` : nid;

            getDexieConnectionRefresh()
                .audios.get(`${config.user.memberId}-${contentNid}`)
                .then((res) => {
                    if (res) {
                        setIsAudioDownloaded(true);
                        setDownloadedAudioUrl(URL.createObjectURL(res.blob));
                    } else {
                        setIsAudioDownloaded(false);
                    }
                })
                .catch(() => {
                    setIsAudioDownloaded(false);
                });
        }
    }, [currentAudio, bundleIndex]);

    const loading =
        playlistLoading ||
        audioLoading ||
        rmgLoading ||
        rmpLoading ||
        playlistsLoading;

    const onComplete = () => {
        handleContentCompleted();
        if (isReceivedMemberAudio) {
            let action = undefined;
            if (isBundle)
                action = {
                    index: bundleIndex + 1,
                };
            logGiftCompleted(action);
        }
        if (playlistControls.loop || mediaIndex < playlistAudios?.length - 1)
            next();
    };
    const url = isAudioDownloaded ? downloadedAudioUrl : mediaURL;

    const miniplayerOptions = useRef<MiniPlayerOptions>(null);
    const alreadyMini = useRef<boolean>(null);

    useEffect(() => {
        if (miniPlayer.url) alreadyMini.current = true;
        else alreadyMini.current = false;
    }, [miniPlayer]);

    useMemo(() => {
        if (playlistAudios)
            miniplayerOptions.current = {
                mediaUrl: url,
                thumbnail: thumbnail,
                title: title,
                singleLoop: isSingleLoop,
                isPlaying: playingState,
                parentLink: getCurrentUrl(),
                content: currentAudio,
                playlist: {
                    data: playlistAudios,
                    playlistId: id,
                    mediaIndex: mediaIndex,
                    bundleIndex: bundleIndex,
                },
            };
    }, [
        playlistAudios,
        url,
        thumbnail,
        title,
        isSingleLoop,
        playingState,
        currentAudio,
        id,
        mediaIndex,
        bundleIndex,
    ]);

    // the mini-player keeps playing this url after unmount, so it must survive
    useOwnedBlobUrl(downloadedAudioUrl, willHandOffToMiniPlayer(miniplayerOptions, alreadyMini));

    const miniPlayerClick = () => {
        const options = miniplayerOptions.current;
        if (options)
            onClickMiniPlayer({
                isAudio: true,
                mediaUrl: options.mediaUrl,
                thumbnail: options.thumbnail,
                title: options.title,
                content: options.content,
                singleLoop: options.singleLoop,
                parentLink: options.parentLink,
                playlist: options.playlist,
                contentType: currentAudio.isReceived ? "GiftMP3" : "MP3",
            })();
    };

    // this useEffect is for component unmounting
    useEffect(() => {
        return () => {
            if (
                miniplayerOptions.current &&
                miniplayerOptions.current.isPlaying &&
                !alreadyMini.current
            )
                miniPlayerClick();
        };
    }, []);

    return (
        <Layout>
            <AppHeader
                title={title}
                canGoBack
                searchable={false}
                onBackClick={() => {
                    navigate(
                        linkProvider.react
                            .playlist()
                            .detail(playlist?.id.toString(), {
                                scrollTo: +queryParams?.scrollTo - 2,
                            }),
                    );
                }}
            />
            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.fetch_playlist}
                />
            )}
            {!loading && (
                <>
                    <Grid container>
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent={"space-between"}
                            >
                                <ForwardBackItem
                                    display={"flex"}
                                    alignItems="center"
                                    onClick={previous}
                                >
                                    <BackIcon icon={["fas", "chevron-left"]} />
                                    <FwdBackTypo
                                        fontWeight={"bold"}
                                        variant="h5"
                                    >
                                        Previous
                                    </FwdBackTypo>
                                </ForwardBackItem>
                                <ForwardBackItem
                                    display={"flex"}
                                    alignItems="center"
                                    onClick={next}
                                >
                                    <FwdBackTypo
                                        fontWeight={"bold"}
                                        variant="h5"
                                    >
                                        Next
                                    </FwdBackTypo>
                                    <FwdIcon icon={["fas", "chevron-right"]} />
                                </ForwardBackItem>
                            </Box>
                        </Grid>
                    </Grid>
                    <PlayerContainer item xs={12}>
                        <MediaPlayer
                            key={forceReloadKey}
                            title={title}
                            playerRef={playerRef}
                            mediaURL={
                                isAudioDownloaded
                                    ? downloadedAudioUrl
                                    : mediaURL
                            }
                            imageURL={thumbnail}
                            onReady={() => onMediaPlayerReady()}
                            progressInterval={PLAYER_PROGRESS_INTERVAL}
                            onProgress={onPlayerProgress}
                            onEnded={onComplete}
                            loop={isSingleLoop}
                            isAudio={true}
                            onNext={next}
                            onPrevious={previous}
                            playing={currentPlayingState && !miniPlayer.url}
                            onPlay={handlePlayerPlay}
                            onPause={handlePlayerPause}
                            loading={!isReadyToPlay}
                            onUpdateExpiredUrl={() => {
                                setIsReady(false);
                                setIsReadyToPlay(true);
                            }}
                            onError={() => {
                                setIsReadyToPlay(true);
                            }}
                        />
                    </PlayerContainer>
                    <Grid
                        display={"flex"}
                        flexDirection={"row"}
                        alignItems={"center"}
                        justifyContent={miniPlayer.url ? "end" : "center"}
                    >
                        <PlayerActionBtns
                            handleChangeSeek={handleChangeSeek}
                            isShuffle={playlistControls.shuffle}
                            isMultiLoop={playlistControls.loop}
                            isLoop={isSingleLoop}
                            onClickShuffle={() => {
                                setQueryParams({
                                    ...queryParams,
                                    index: sortedMappedAudios
                                        .findIndex(
                                            (a) => a.nid === currentAudio.nid,
                                        )
                                        .toString(),
                                });
                                toggleGlobalShuffle();
                                setIsReady(false);
                            }}
                            onClickMultiLoop={() => toggleGlobalLoop()}
                            onClickLoop={() => setSingleLoop(!isSingleLoop)}
                            onClickMiniPlayer={miniPlayerClick}
                        />
                    </Grid>
                    {playlistAudios && (
                        <Grid item xs={12} marginTop={1}>
                            <Grey800Typography variant="body2">
                                {currentAudio?.description}
                            </Grey800Typography>
                        </Grid>
                    )}
                </>
            )}
        </Layout>
    );
};

const useFirebaseEvents = (
    currentAudio: GiftWithMedia | ContentWithMedia,
    isAudioDownloaded: boolean,
    isBundle: boolean,
    bundleIndex: number,
) => {
    const { logFirebaseEvent } = useFirebaseLogger();

    const getAction = () => ({
        action: !isAudioDownloaded
            ? EventActions.LIVE_STREAM
            : EventActions.PLAY,
        nId: currentAudio?.nid,
        category: currentAudio?.isReceived
            ? EventCategories.GIFT_AUDIOS
            : EventCategories.AUDIOS,
        contentTitle: isBundle
            ? currentAudio?.media[bundleIndex]?.media_title
            : currentAudio?.title,
        skuId: isBundle
            ? currentAudio?.media[bundleIndex]?.media_file_name
            : currentAudio?.sku_id,
    });

    useEffect(() => {
        if (isAudioDownloaded === null || !currentAudio?.media) return;

        const getEventName = () =>
            currentAudio.isReceived
                ? !isAudioDownloaded
                    ? EventNames.GIFT_AUDIO_LIVESTREAM
                    : EventNames.GIFT_AUDIO_PLAYED
                : !isAudioDownloaded
                  ? EventNames.AUDIO_LIVE_STREAM
                  : EventNames.AUDIO_PLAYED;

        logFirebaseEvent(getEventName(), getAction());
    }, [currentAudio?.media, isAudioDownloaded, bundleIndex]);

    useEffect(() => {
        logFirebaseEvent(
            currentAudio?.isReceived
                ? EventNames.GIFT_AUDIO_OPENED
                : EventNames.AUDIO_OPENED,
            {
                ...getAction(),
                action: EventActions.OPEN,
            },
        );
    }, [currentAudio?.nid]);
};
